import React, { useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { ImagePlus, Star, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

function getImageLimit(profile, maxImagesProp) {
  // Si le parent fournit explicitement un max, on le respecte.
  if (typeof maxImagesProp === "number") {
    return maxImagesProp < 0 ? Infinity : maxImagesProp;
  }

  if (!profile) {
    // Par défaut : user standard
    return 5;
  }

  const planRaw =
    (profile.plan ||
      profile.subscription_plan ||
      profile.current_plan ||
      "").toString().toLowerCase();
  const roleRaw =
    (profile.role || profile.user_role || "").toString().toLowerCase();

  const isFamilyOffice =
    planRaw.includes("family") || roleRaw.includes("family_office");

  if (isFamilyOffice) {
    // Family Office : vue seule, pas d’upload
    return 0;
  }

  const isAdvisor =
    roleRaw.includes("advisor") || planRaw.includes("advisor");

  const isPremium =
    planRaw.includes("premium") ||
    planRaw.includes("prm") ||
    planRaw.includes("plus");

  // Art Advisor Premium : illimité
  if (isAdvisor && isPremium) return Infinity;

  // Art Advisor Standard : 10
  if (isAdvisor) return 10;

  // User Premium : 10
  if (isPremium) return 10;

  // User Standard : 5
  return 5;
}

export default function ArtworkImageManager({
  images = [],
  onAddImages,
  onRemoveImage,
  onSetPrimaryImage,
  readOnly = false,
  maxImages: maxImagesProp, // optionnel, pour override
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const { profile } = useAuth() || {};

  const maxImages = useMemo(
    () => getImageLimit(profile, maxImagesProp),
    [profile, maxImagesProp]
  );

  const imagesCount = Array.isArray(images) ? images.length : 0;
  const hasLimit = Number.isFinite(maxImages);
  const canUploadMore =
    !readOnly && (maxImages === 0 ? false : !hasLimit || imagesCount < maxImages);

  const handleClickUpload = () => {
    if (!canUploadMore) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;
    if (!onAddImages) return;

    // Si quota actif, on coupe à la limite
    if (hasLimit && imagesCount >= maxImages) {
      alert(
        t("artworkImageManager.quotaReached")
      );
      event.target.value = "";
      return;
    }

    let allowedFiles = files;
    if (hasLimit) {
      const remaining = maxImages - imagesCount;
      if (remaining <= 0) {
        alert(
          t("artworkImageManager.quotaReached")
        );
        event.target.value = "";
        return;
      }
      allowedFiles = files.slice(0, remaining);
      if (allowedFiles.length < files.length) {
        alert(
          t("artworkImageManager.onlyRemaining", { count: remaining })
        );
      }
    }

    onAddImages(allowedFiles);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (!canUploadMore) return;

    const files = Array.from(event.dataTransfer.files || []);
    if (!files.length || !onAddImages) return;

    if (hasLimit && imagesCount >= maxImages) {
      alert(
        t("artworkImageManager.quotaReached")
      );
      return;
    }

    let allowedFiles = files;
    if (hasLimit) {
      const remaining = maxImages - imagesCount;
      if (remaining <= 0) {
        alert(
          t("artworkImageManager.quotaReached")
        );
        return;
      }
      allowedFiles = files.slice(0, remaining);
      if (allowedFiles.length < files.length) {
        alert(
          t("artworkImageManager.onlyRemaining", { count: remaining })
        );
      }
    }

    onAddImages(allowedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const renderImageSrc = (img) => {
    if (!img) return "";
    return img.url || img.path || img.image_url || "";
  };

  return (
    <div className="space-y-4">
      {/* Zone principale d’upload */}
      <div
        className={[
          "border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center transition",
          canUploadMore
            ? "border-gray-300 hover:border-black cursor-pointer"
            : "border-gray-200 bg-gray-50 cursor-not-allowed",
        ].join(" ")}
        onClick={handleClickUpload}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <ImagePlus className="w-8 h-8 mb-3 text-gray-400" />
        <p className="font-medium text-gray-800 mb-1">
          {canUploadMore
            ? t("artworkImageManager.uploadPrompt")
            : t("artworkImageManager.uploadDisabled")}
        </p>
        <p className="text-xs text-gray-500">
          {t("artworkImageManager.formats")}
        </p>

        {hasLimit && (
          <p className="mt-2 text-xs text-gray-500">
            {t("artworkImageManager.usage", {
              count: imagesCount,
              max: maxImages === Infinity ? t("artworkImageManager.unlimited") : maxImages,
            })}
          </p>
        )}

        {maxImages === 0 && (
          <p className="mt-2 text-xs text-gray-500">
            {t("artworkImageManager.familyOfficeNote")}
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFilesSelected}
          disabled={!canUploadMore}
        />
      </div>

      {/* Vignettes */}
      {imagesCount > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {images.map((img) => {
            const src = renderImageSrc(img);
            if (!src) return null;

            const isPrimary = Boolean(img.isPrimary);

            return (
              <div
                key={img.id || src}
                className="relative group rounded-lg overflow-hidden border border-gray-200"
              >
                <img
                  src={src}
                  alt={img.alt || t("artworkImageManager.artworkAlt")}
                  className="w-full h-40 object-cover"
                />

                {/* Badge image principale */}
                <button
                  type="button"
                  className={[
                    "absolute top-2 left-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm",
                    isPrimary
                      ? "bg-black text-white"
                      : "bg-white/90 text-gray-700 hover:bg-white",
                  ].join(" ")}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSetPrimaryImage) {
                      onSetPrimaryImage(img.id);
                    }
                  }}
                >
                  <Star
                    className={
                      "w-3 h-3 mr-1 " + (isPrimary ? "fill-current" : "")
                    }
                  />
                  {isPrimary ? t("artworkImageManager.primary") : t("artworkImageManager.setCover")}
                </button>

                {/* Bouton suppression — toutes les images */}
                {onRemoveImage && (
                  <button
                    type="button"
                    className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/90 text-gray-700 shadow-sm hover:bg-red-50 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      const msg = isPrimary
                        ? t("artworkImageManager.deletePrimaryConfirm")
                        : t("artworkImageManager.deleteConfirm");
                      if (window.confirm(msg)) {
                        onRemoveImage(img.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}